import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import AuthModal from '../components/AuthModal';
import { createReducedParty } from '../services/reducedPartyActions';
import {
  reducedModeEnabled,
  reducedModeHighlights,
  reducedModeMessage,
} from '../utils/reducedMode';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<'createParty' | null>(null);
  const [joinCode, setJoinCode] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const pendingHandled = useRef(false);
  // Phone mic mode removed; main party supports phones without a separate mode

  // Basic environment detection for better iOS/in-app guidance
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isInApp = /(FBAN|FBAV|Instagram|Twitter|Line|LinkedInApp)/i.test(ua);

  const handleSignIn = () => {
    setShowAuthModal(true);
  };

  const handleGetStarted = async () => {
    if (user) {
      navigate('/dashboard');
    } else {
      // Show auth modal for sign in
      setShowAuthModal(true);
    }
  };

  const handleStartParty = () => {
    if (user) {
      createFreeParty();
    } else {
      setPendingAction('createParty');
      // Persist pending action so it survives mobile redirect sign-in
      try { sessionStorage.setItem('qroke-pending-action', 'createParty'); } catch {}
      setShowAuthModal(true);
    }
  };

  // After mobile redirect sign-in, auto-execute the pending action
  useEffect(() => {
    if (authLoading || pendingHandled.current) return;
    if (!user) return;
    
    let storedAction: string | null = null;
    try { storedAction = sessionStorage.getItem('qroke-pending-action'); } catch {}
    
    if (storedAction === 'createParty') {
      pendingHandled.current = true;
      try { sessionStorage.removeItem('qroke-pending-action'); } catch {}
      // Small delay to let Firebase fully initialize
      setTimeout(() => createFreeParty(), 300);
    }
  }, [user, authLoading]);

  const handleJoinParty = () => {
    const code = joinCode.trim();
    if (code) {
      navigate(`/search/${code}`);
    }
  };

  const createFreeParty = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      let partyId: string;
      if (reducedModeEnabled) {
        ({ partyId } = await createReducedParty('Free Karaoke Party', {
          maxQueueSize: 5,
          requireAuth: false,
          tier: 'free',
        }));
      } else {
        const functions = getFunctions(undefined, 'us-central1');
        const createParty = httpsCallable(functions, 'party');
        const result = await createParty({
          action: 'create',
          name: 'Free Karaoke Party',
          settings: {
            maxQueueSize: 5,
            requireAuth: false,
            tier: 'free'
          }
        });
        ({ partyId } = result.data as any);
      }
      navigate(`/party/${partyId}`);
    } catch (error) {
      console.error('Error creating free party:', error);
      toast('Failed to create party. Please try again.', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const handleGetPartyPass = () => {
    navigate('/subscription');
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-qr-dark via-gray-900 to-qr-dark text-white">
      {/* iOS In-App Browser warning (Google Sign-In may not complete) */}
      {isIOS && isInApp && (
        <div className="bg-yellow-500/10 text-yellow-300 border-b border-yellow-500/30 px-4 py-3 text-sm text-center">
          It looks like you're in an in-app browser (e.g., Instagram/Facebook). Google Sign-In may not complete here.
          <span className="hidden sm:inline"> Please open this page in Safari and try again.</span>
        </div>
      )}
      {/* Header */}
      <header className="relative z-10 p-6">
        <div className="flex justify-between items-center max-w-7xl mx-auto">
          <div className="flex items-center space-x-3">
            <img src="/Qroke Logo.png" alt="QrOke Logo" className="w-10 h-10 rounded-lg" />
            <span className="text-white font-display font-bold text-2xl">QrOke</span>
          </div>
          
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-gray-300 hover:text-white transition-colors">
              Features
            </a>
            <a href="#pricing" className="text-gray-300 hover:text-white transition-colors">
              Pricing
            </a>
            <button
              onClick={() => navigate('/instructions')}
              className="text-gray-300 hover:text-white transition-colors"
            >
              Instructions
            </button>
            <button
              onClick={handleStartParty}
              className="bg-qr-primary hover:bg-qr-secondary px-4 py-2 rounded-lg text-white font-medium transition-colors"
            >
              Start Party
            </button>
            {user ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-qr-primary hover:bg-qr-secondary px-6 py-2 rounded-lg text-white font-medium transition-colors"
              >
                Dashboard
              </button>
            ) : (
              <button
                onClick={handleSignIn}
                className="bg-qr-primary hover:bg-qr-secondary px-6 py-2 rounded-lg text-white font-medium transition-colors"
              >
                Sign In
              </button>
            )}
          </nav>

          {/* Mobile auth control */}
          <div className="md:hidden flex items-center gap-2">
            {/* Mobile: Instructions link */}
            <button
              onClick={() => navigate('/instructions')}
              className="px-3 py-2 text-sm text-gray-200 border border-white/20 rounded-lg"
            >
              Instructions
            </button>
            {/* Mobile: Start Party */}
            <button
              onClick={handleStartParty}
              className="bg-qr-primary hover:bg-qr-secondary px-3 py-2 rounded-lg text-white font-medium transition-colors"
            >
              Start
            </button>
            {user ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-qr-primary hover:bg-qr-secondary px-4 py-2 rounded-lg text-white font-medium transition-colors"
                aria-label="Open Dashboard"
              >
                Dashboard
              </button>
            ) : (
              <button
                onClick={handleSignIn}
                className="bg-qr-primary hover:bg-qr-secondary px-4 py-2 rounded-lg text-white font-medium transition-colors"
                aria-label="Sign In"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="flex-1 flex items-center justify-center px-6 relative">
        <div className="absolute inset-0 qr-gradient opacity-10"></div>
        
        <div className="relative z-10 text-center max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-display font-bold text-white mb-6">
            The Ultimate
            <span className="block qr-gradient bg-clip-text text-transparent">
              Karaoke Experience
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 mb-6 leading-relaxed">
            {reducedModeEnabled
              ? 'Host a clean, fast karaoke night with QR song requests, a simple live queue, and lightweight song search. No apps to install - just scan and sing.'
              : 'Host epic karaoke parties with QR code song requests, real-time queue management, and seamless YouTube integration. No apps to install - just scan and sing!'}
          </p>
          <div className="mx-auto max-w-3xl text-sm text-yellow-200 bg-yellow-900/30 border border-yellow-600/40 rounded-lg px-4 py-3 mb-8">
            <strong>Streaming Notice:</strong> YouTube integration is for personal, at‑home use only. Commercial use of streamed content may violate provider terms and copyright law.
            <button onClick={() => navigate('/content-sources')} className="ml-2 underline text-yellow-100 hover:text-white">Learn more</button>
          </div>

          {reducedModeEnabled && (
            <div className="mx-auto mb-8 max-w-3xl text-left">
              <p className="text-sm leading-6 text-gray-200">{reducedModeMessage}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {reducedModeHighlights.map((highlight) => (
                  <span
                    key={highlight}
                    className="rounded-full border border-white/12 bg-black/20 px-3 py-1 text-xs font-medium text-gray-100"
                  >
                    {highlight}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={handleStartParty}
              disabled={isCreating}
              className="bg-qr-primary hover:bg-qr-secondary px-8 py-4 rounded-xl text-white font-semibold text-lg transition-all transform hover:scale-105 qr-shadow disabled:opacity-60 disabled:cursor-wait disabled:transform-none"
            >
              {isCreating ? 'Creating Party...' : 'Start Party'}
            </button>
          </div>

          {/* Join a Party */}
          <div className="mt-8 flex flex-col items-center">
            <p className="text-gray-400 text-sm mb-2">Have a party code? Join as a guest:</p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleJoinParty()}
                placeholder="Enter Party Code"
                className="px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-qr-primary w-48"
              />
              <button
                onClick={handleJoinParty}
                disabled={!joinCode.trim()}
                className="px-6 py-3 rounded-lg bg-white/20 hover:bg-white/30 text-white font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Join
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-display font-bold text-white text-center mb-16">
            {reducedModeEnabled ? 'Lean Karaoke Essentials' : 'Why Choose QrOke?'}
          </h2>
          
          <div className={`grid gap-8 ${reducedModeEnabled ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'}`}>
            <div className="text-center">
              <div className="w-16 h-16 qr-gradient rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">📱</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">QR Join Flow</h3>
              <p className="text-gray-300 mb-3">
                Guests scan a QR code to join fast and request songs without installing anything.
              </p>
              <button onClick={() => navigate('/instructions#hosting')} className="text-qr-primary hover:underline text-sm">Learn how →</button>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 qr-gradient rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🎵</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Lean Search</h3>
              <p className="text-gray-300 mb-3">
                Use lightweight browser search when available, or fall back to a host-published local catalog.
              </p>
              <button onClick={() => navigate('/instructions#search')} className="text-qr-primary hover:underline text-sm">Learn how →</button>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 qr-gradient rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Live Queue</h3>
              <p className="text-gray-300 mb-3">
                Keep the next singer moving with a clear queue and simple playback handoff between songs.
              </p>
              <button onClick={() => navigate('/instructions#queue')} className="text-qr-primary hover:underline text-sm">Learn how →</button>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 qr-gradient rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🎭</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Playback Display</h3>
              <p className="text-gray-300 mb-3">
                Keep lyrics and QR join instructions visible on the main screen with minimal setup.
              </p>
              <button onClick={() => navigate('/instructions#external-display')} className="text-qr-primary hover:underline text-sm">Learn how →</button>
            </div>

            {!reducedModeEnabled && (
              <>
                <div className="text-center">
                  <div className="w-16 h-16 qr-gradient rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <span className="text-2xl">🎨</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Custom Branding</h3>
                  <p className="text-gray-300 mb-3">
                    Add your logo, colors, and venue name to splash screens. Included with Party Pass.
                  </p>
                  <button onClick={() => navigate('/instructions#branding')} className="text-qr-primary hover:underline text-sm">Learn how →</button>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 qr-gradient rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <span className="text-2xl">📺</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Venue Ads</h3>
                  <p className="text-gray-300 mb-3">
                    Display your own image or video ads between songs. Promote specials and sponsors.
                  </p>
                  <button onClick={() => navigate('/instructions#venue-ads')} className="text-qr-primary hover:underline text-sm">Learn how →</button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6 bg-white/5">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-display font-bold text-white text-center mb-16">
            {reducedModeEnabled ? 'Lean Access' : 'Simple, Fair Pricing'}
          </h2>
          
          {reducedModeEnabled ? (
            <div className="grid gap-8 md:grid-cols-2">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-4">Lean Free Tier</h3>
                <div className="text-4xl font-bold text-white mb-6">$0</div>
                <ul className="space-y-3 text-gray-300 mb-8">
                  <li>✓ Party launch and QR join</li>
                  <li>✓ Lightweight song search or local catalog</li>
                  <li>✓ Live queue and playback display</li>
                  <li>✓ Free-tier promo placements may appear</li>
                </ul>
                <button 
                  onClick={handleStartParty}
                  className="w-full bg-white/20 hover:bg-white/30 py-3 rounded-lg text-white font-semibold transition-colors"
                >
                  Start Lean Party
                </button>
              </div>

              <div className="bg-stone-100/10 backdrop-blur-sm border border-stone-200/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-4">Need Something Back?</h3>
                <div className="text-2xl font-bold text-white mb-6">Tell Us What Matters</div>
                <p className="text-gray-300 mb-6">
                  If a missing feature would change whether you can use QrOke, send that request directly to the admin inbox.
                </p>
                <ul className="space-y-3 text-gray-300 mb-8">
                  <li>✓ Request missing host tools</li>
                  <li>✓ Flag venue or KJ blockers</li>
                  <li>✓ Prioritize what returns first</li>
                  <li>✓ Route feedback straight to admin</li>
                </ul>
                <button
                  onClick={() => navigate('/feedback?from=/')}
                  className="w-full bg-qr-primary hover:bg-qr-secondary py-3 rounded-lg text-white font-semibold transition-colors"
                >
                  Give Feedback / Request Feature
                </button>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-4">Free</h3>
                <div className="text-4xl font-bold text-white mb-6">$0</div>
                <ul className="space-y-3 text-gray-300 mb-8">
                  <li>✓ Up to 5 songs in queue</li>
                  <li>✓ Basic features</li>
                  <li>✓ Banner ads</li>
                  <li>✓ 1 hour sessions</li>
                </ul>
                <button 
                  onClick={handleStartParty}
                  className="w-full bg-white/20 hover:bg-white/30 py-3 rounded-lg text-white font-semibold transition-colors"
                >
                  Start Free
                </button>
              </div>

              <div className="bg-qr-primary/20 backdrop-blur-sm border-2 border-qr-primary rounded-2xl p-8 relative">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-qr-primary px-4 py-1 rounded-full text-white text-sm font-semibold">
                  Most Popular
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Party Pass</h3>
                <div className="text-4xl font-bold text-white mb-6">$2<span className="text-lg text-gray-300">/party</span> <span className="text-lg text-gray-300">or $5/month</span></div>
                <ul className="space-y-3 text-gray-300 mb-8">
                  <li>✓ 12 hours per party</li>
                  <li>✓ Unlimited queue & no ads</li>
                  <li>✓ Custom party branding</li>
                  <li>✓ Venue ads between songs</li>
                  <li>✓ Priority support</li>
                </ul>
                <button 
                  onClick={handleGetPartyPass}
                  className="w-full bg-qr-primary hover:bg-qr-secondary py-3 rounded-lg text-white font-semibold transition-colors"
                >
                  Get Party Pass
                </button>
              </div>

              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-4">For Venues & Pros</h3>
                <div className="text-2xl font-bold text-white mb-6">Contact Us</div>
                <p className="text-gray-300 mb-6">
                  Running a bar, restaurant, or professional KJ event? Contact us for custom branding and venue solutions.
                </p>
                <ul className="space-y-3 text-gray-300 mb-8">
                  <li>✓ Custom branding & themes</li>
                  <li>✓ Venue-specific features</li>
                  <li>✓ Professional support</li>
                  <li>✓ Volume pricing</li>
                </ul>
                <a 
                  href="mailto:bkentner@gmail.com?subject=QrOke%20Pro&body=Hi%2C%20I'm%20interested%20in%20QrOke%20for%20my%20venue%2Fevent.%20Please%20contact%20me%20about%20custom%20solutions."
                  className="w-full bg-qr-accent hover:bg-yellow-500 py-3 rounded-lg text-white font-semibold transition-colors inline-block text-center"
                >
                  Contact Us
                </a>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-8 h-8 qr-gradient rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">Q</span>
            </div>
            <span className="text-white font-display font-bold text-xl">QrOke</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-center text-gray-300 text-sm">
            <a href="https://qroke.org/terms-of-service" target="_blank" rel="noopener" className="hover:text-white">Terms of Service</a>
            <span className="hidden sm:inline">•</span>
            <a href="https://qroke.org/privacy-policy-for-qroke" target="_blank" rel="noopener" className="hover:text-white">Privacy Policy</a>
            <span className="hidden sm:inline">•</span>
            <button onClick={() => navigate('/content-sources')} className="hover:text-white">Content Sources & Licensing</button>
          </div>
          <p className="mt-3 text-gray-400">© {new Date().getFullYear()} QrOke. Making karaoke parties awesome, one QR code at a time.</p>
        </div>
      </footer>
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => {
          setShowAuthModal(false);
          // If user just signed in via popup and had a pending action, execute it
          if (pendingAction === 'createParty') {
            setPendingAction(null);
            try { sessionStorage.removeItem('qroke-pending-action'); } catch {}
            // Small delay to let auth state propagate
            setTimeout(() => createFreeParty(), 500);
          }
        }} 
      />
    </div>
  );
};

export default LandingPage;